import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Typography,
} from '@mui/material';
import {
  Dashboard,
  Flight,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../store';
import { switchRole } from '../store/slices/authSlice';
import { useTranslation } from 'react-i18next';

interface RoleSwitcherProps {
  onClose?: () => void;
}

const RoleSwitcher: React.FC<RoleSwitcherProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  const user = useSelector((state: RootState) => state.auth.user);
  const switchingRole = useSelector((state: RootState) => state.auth.switchingRole);

  // Check if user has both club_manager and pilot roles
  const availableRoles = user?.available_roles || [];
  const hasClubManagerRole = availableRoles.includes('club_manager') || availableRoles.includes('admin');
  const hasPilotRole = availableRoles.includes('pilot');
  const isDualRole = hasClubManagerRole && hasPilotRole;

  // Determine current active view
  const getCurrentView = (): 'manager' | 'pilot' => {
    if (user?.login_mode === 'pilot') return 'pilot';
    return 'manager';
  };

  const currentView = getCurrentView();

  // Don't render if user doesn't have dual role (club_manager/admin + pilot)
  if (!isDualRole) {
    return null;
  }

  const handleViewChange = async (
    _event: React.MouseEvent<HTMLElement>,
    newView: 'manager' | 'pilot' | null
  ) => {
    if (!newView || newView === currentView || switchingRole) return;

    const targetRole = newView === 'pilot' ? 'pilot' : 'club_manager';

    try {
      const result = await dispatch(switchRole(targetRole)).unwrap();
      if (onClose) onClose();
      navigate(result.redirect_path);
    } catch (error) {
      console.error('Failed to switch role:', error);
    }
  };

  return (
    <Box sx={{ px: 2, py: 1.5 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 1 }}
      >
        {t('roleSwitcher.switchView', 'Switch View')}
      </Typography>
      <ToggleButtonGroup
        value={currentView}
        exclusive
        onChange={handleViewChange}
        size="small"
        fullWidth
        disabled={switchingRole}
        sx={{
          '& .MuiToggleButton-root': {
            py: 0.75,
            textTransform: 'none',
            fontSize: '0.8rem',
          },
        }}
      >
        <ToggleButton value="manager">
          {switchingRole && currentView !== 'manager' ? (
            <CircularProgress size={16} sx={{ mr: 0.5 }} />
          ) : (
            <Dashboard sx={{ fontSize: 18, mr: 0.5 }} />
          )}
          {t('userType.clubManager', 'Manager')}
        </ToggleButton>
        <ToggleButton value="pilot">
          {switchingRole && currentView !== 'pilot' ? (
            <CircularProgress size={16} sx={{ mr: 0.5 }} />
          ) : (
            <Flight sx={{ fontSize: 18, mr: 0.5 }} />
          )}
          {t('userType.pilot', 'Pilot')}
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
};

export default RoleSwitcher;
